<template>
    <div class="sankey-v2">
        <div class="chart-wrapper" :class="{ 'no-journey': !journeyId }">
            <div class="chart" :class="{ 'is-loading': isLoading }" ref="chart" />
            <loading-box v-if="isLoading" />
        </div>
        
        <touch-point-info v-if="displayTouchpointInfo"
            ref="touchpointInfo"
            class="touchpoint-info"
            @close="selectedBrickId = null"
        />
    </div>
</template>

<script>
    import {createNamespacedHelpers} from 'vuex';
    import $ from 'jquery';

    import {bootstrapDangerGrowl as errorGrowl} from "@core/Util/growler";
    import createChart from './draw-chart';
    import {supportsStatistics} from './helpers';

    import LoadingBox from "@core/Components/Util/LoadingBox";
    import TouchPointInfo from "./TouchPointInfo";
    import Loader from "@modules/JourneyBuilder/Components/Elements/Loader";
    
    const {mapState, mapGetters, mapMutations, mapActions} = createNamespacedHelpers('journeyBuilder/reporting');
    
    export default {
        name: "SankeyV2",
        components: {
            Loader,
            LoadingBox,
            TouchPointInfo
        },
        async mounted() {
            await this.drawChartFromRemoteData();
        },
        beforeDestroy() {
            if(this.activeChart){
                this.activeChart.destroy();
            }
        },
        methods: {
            ...mapMutations(['setSankeyData', 'setSelectedBrickId']),
            ...mapActions(['loadSankeyData']),
            async drawChartFromRemoteData(){
                try {
                    this.isLoading = true;
                    
                    await this.loadSankeyData();
                    
                    //Wait for our computed values (chartData/chartTypes) to update
                    await this.$nextTick();
                    
                    const data = this.chartData;
                    const types = this.chartTypes;
                    
                    //Ensure we have a chart; if not, initialize
                    if(this.activeChart) {
                        await this.refreshChart(data, types);
                    } else {
                        await this.initializeChart(data, types);
                    }

                    this.activeChart.setHighlightMode(this.highlightMode);
                } catch(e){
                    console.error(e);
                    errorGrowl(e);
                } finally {
                    this.isLoading = false;
                }
            },
            async initializeChart(data, types){
                this.activeChart = await createChart(
                    this.$refs['chart'],
                    {
                        nodes: data,
                        types: types
                    },
                    {
                        onNodeSelected: brickId => this.selectedBrickId = brickId
                    }
                );
            },
            async refreshChart(data, types) {
                await this.activeChart.setData({
                    nodes: data,
                    types: types
                });
            }
        },
        computed: {
            ...mapGetters(['watchableReportStatisticsParameters', 'reportStatisticsParameters', 'selectedNodes']),
            ...mapState({
                'journeyId': 'journeyId',
                'storedSelectedBrickId': 'selectedBrickId',
                'chartData': 'sankeyData',
                'chartTypes': 'sankeyTypes'
            }),
            selectedBrickId: {
                get(){
                    return this.storedSelectedBrickId;
                },
                set(value){
                    this.setSelectedBrickId(value);
                }
            },
            displayTouchpointInfo(){
                if(this.selectedNodes.length){
                    //All node types are expected to be the same 
                    return supportsStatistics(this.selectedNodes[0]);
                }
                
                return false;
            }
        },
        watch: {
            watchableReportStatisticsParameters(){
                this.drawChartFromRemoteData();
            },
            journeyId(){
                this.selectedBrickId = undefined;
            },
            activeChart(value){
                //selectedNodeId/highlightMode may be selected prior to our chart having been rendered
                //when a selectedNodeId was provided prior to chart render, the nodeId is preselected. Preselected nodes get autofocus.
                if(this.selectedBrickId) {
                    value.selectBrick(this.selectedBrickId);
                    value.centerBrick(this.selectedBrickId);
                }
                
                if(this.highlightMode) value.setHighlightMode(this.highlightMode);
            },
            selectedBrickId(value) {
                if(this.activeChart){
                    this.activeChart.selectBrick(value);
                }
            },
            highlightMode(value){
                if(this.activeChart){
                    this.activeChart.setHighlightMode(value);   
                }
            },
            async displayTouchpointInfo(value){
                //Not unsticking our sticky element prior to deletion causes errors upon re-stickying a new element
                if(!value){
                    $(this.$refs.touchpointInfo.$el).unstick();
                    return;
                }
                
                //On addition to the DOM - Make element sticky, scroll along with user
                await this.$nextTick();

                const headerHeight = document.querySelector('.main-header')
                    .getBoundingClientRect()
                    .height;
                
                $(this.$refs.touchpointInfo.$el).sticky({
                    topSpacing: headerHeight,
                    widthFromWrapper: false
                }).sticky('update');
            }
        },
        data: () => ({
            isLoading: false,
            activeChart: undefined
        }),
        props: {
            highlightMode: {
                type: String,
                required: false
            }
        }
    }
</script>

<style src="./chart-content.scss" lang="scss" />
<style src="./sankey-v2.scss" scoped lang="scss" />